import React, { use, useCallback, useEffect, useMemo } from "react";
import {
    ColorValue,
    LayoutChangeEvent,
    StyleProp,
    StyleSheet,
    View,
    ViewStyle,
} from "react-native";
import { useTheme } from "../hooks/use-theme";
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from "react-native-reanimated";
import { StepsManagerContext } from "../contexts/StepsManager";
import { BUBBLE_ANIMATION } from "../components/Bubble";

export type StepInfo = {
    isVisible: boolean;
};

const STEP_MARGIN = 4;

type StepInfoContent = StepInfo & {
    name: string;
};

export type StepsConfig = {
    seenColor?: ColorValue;
    unseenColor?: ColorValue;
    currentColor?: ColorValue;

    currentStep?: {
        component?: React.ReactElement;
        size?: number;
    };
};

type StepProps = {
    isCurrent?: boolean;
    isSeen?: boolean;
    style?: StyleProp<ViewStyle>;
    isVisible?: boolean;
    stepsConfig?: StepsConfig;
};

type ReplaceType<T, A, B> = {
    [K in keyof T]: T[K] extends A | undefined ? Exclude<T[K], A> | B : T[K];
};

export const STEPS_DURATION = 300;

const STEPS_ANIMATION = {
    duration: STEPS_DURATION,
};

// isCurrent = [false, true]
const SIZES_VALUES = [5, 10];

const OPACITY_VALUES = [0, 1];

const Step = ({ isCurrent = false, isSeen = false, style, stepsConfig }: StepProps) => {
    const { colors } = useTheme();
    const styles = useStyles();

    const {
        seenColor = colors.primary,
        unseenColor = colors.backdrop,
        currentStep: { component, size: currentStepSize } = {
            component: null,
            size: null,
        },
    } = (stepsConfig as ReplaceType<StepsConfig, ColorValue, string>) || {};

    const currentColor = (stepsConfig?.currentColor as string) || seenColor;

    // Prop-derived so the component renders the correct colors on any (re)mount: current, already
    // seen (index < currentIndex), or not yet reached.
    const resolvedColor = isCurrent ? currentColor : isSeen ? seenColor : unseenColor;

    const size = useSharedValue(SIZES_VALUES[+isCurrent]);
    const color = useSharedValue(resolvedColor);

    const animatedStepStyle = useAnimatedStyle(() => ({
        width: size.value,
        height: size.value,
        backgroundColor: color.value,
    }));

    const componentOpacity = useSharedValue(OPACITY_VALUES[+isCurrent]);
    const animatedComponentStyle = useAnimatedStyle(() => ({
        opacity: componentOpacity.value,
    }));

    useEffect(() => {
        color.value = withTiming(resolvedColor, STEPS_ANIMATION);
        size.value = withTiming(
            isCurrent ? currentStepSize || SIZES_VALUES[1] : SIZES_VALUES[0],
            BUBBLE_ANIMATION,
        );

        if (component) {
            componentOpacity.value = withTiming(OPACITY_VALUES[+isCurrent], STEPS_ANIMATION);
        }
    }, [color, component, componentOpacity, currentStepSize, isCurrent, resolvedColor, size]);

    return (
        <Animated.View style={[styles.step, animatedStepStyle, style]}>
            {component && (
                <Animated.View style={[{ width: "100%", height: "100%" }, animatedComponentStyle]}>
                    {component}
                </Animated.View>
            )}
        </Animated.View>
    );
};

export type StepsProps = {
    index: number;
    style?: StyleProp<ViewStyle>;
    testID?: string;
    count?: number;
    stepsInfo?: Array<StepInfo>;
    stepsConfig?: StepsConfig;
};

const Steps: React.FC<StepsProps> = ({
    index: currentIndex,
    style,
    testID,
    count = undefined,
    stepsInfo = undefined,
    stepsConfig,
}) => {
    const styles = useStyles();
    const steps: StepInfoContent[] = useMemo(() => {
        if (count !== undefined) {
            return [...Array(count).keys()].map((_, index) => ({
                isVisible: true,
                name: `step-${index}`,
            }));
        } else if (stepsInfo !== undefined) {
            return stepsInfo.map((step, index) => ({ ...step, name: `step-${index}` }));
        }
        return [];
    }, [count, stepsInfo]);

    const { setSteps } = use(StepsManagerContext);

    const onContainerLayout = useCallback(
        (e: LayoutChangeEvent) => {
            const { x } = e.nativeEvent.layout;
            const values = steps
                .filter((v) => v.isVisible)
                .map((_, index) => x + STEP_MARGIN + index * (SIZES_VALUES[0] + STEP_MARGIN * 2));

            setSteps(values);
        },
        [setSteps, steps],
    );

    return (
        <View onLayout={onContainerLayout} style={[styles.stepsContainer, style]} testID={testID}>
            {steps.map(({ name, isVisible }: StepInfoContent, index) => {
                const isCurrent = index === currentIndex;
                const isSeen = index < currentIndex;

                return isVisible ? (
                    <Step
                        key={name}
                        isCurrent={isCurrent}
                        isSeen={isSeen}
                        stepsConfig={stepsConfig}
                    />
                ) : null;
            })}
        </View>
    );
};

const useStyles = () => {
    return useMemo(
        () =>
            StyleSheet.create({
                stepsContainer: {
                    flexDirection: "row",
                    alignItems: "center",
                    position: "relative",
                    justifyContent: "center",
                },
                step: {
                    marginHorizontal: STEP_MARGIN,
                    borderRadius: 100,
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                },
            }),
        [],
    );
};

export default Steps;
